import {
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { SEX_LIST } from '@/common/constants/common';
import { useGetDicListQuery } from '@/api/common';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import {
	useEditPersonalMutation,
	useGetAdmissionRequestDetailQuery,
} from '@/api/member-management/admissionRequest';
import { EditPersonalRequest } from '@/api/DTO/member-management/admissionRequest';
import AppAgeInput from '@/common/components/form-inputs/AppAgeInput';
import {
	ageSchemaFields,
	toAgeFormValues,
	toAgePayload,
} from '@/utils/ageHelper';

interface Props {
	type: string;
}
export default function EditPersonalInformation(props: Props) {
	const { type } = props;
	const memberType = i18n.t(
		type === 'admissionRequest'
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId } = useParams();
	const [editPersonal] = useEditPersonalMutation();

	const { data: details } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: maritalStatusDicList } = useGetDicListQuery('marital_status', {
		refetchOnMountOrArgChange: true,
	});
	const { data: placeBirthDicList } = useGetDicListQuery('place_of_birth', {
		refetchOnMountOrArgChange: true,
	});
	const { data: dialectDicList } = useGetDicListQuery('dialect', {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<any> = {
		sex: null,
		...toAgeFormValues(),
	};

	const formSchema = Yup.object().shape({
		nameEn: Yup.string().required(t('errorMessage.pleaseEnter')),
		nameZh: Yup.string().required(t('errorMessage.pleaseEnter')),
		...ageSchemaFields(t, {
			getOriginalEstimatedAge: () => details?.data?.estimatedAge,
		}),
		sex: Yup.string().required(t('errorMessage.pleaseSelect')),
		// placeOfBirth: Yup.string().required(t('errorMessage.pleaseSelect')),
		placeOfBirthSpecify: Yup.string().when('placeOfBirth', {
			is: (placeOfBirth: string) => placeOfBirth && placeOfBirthIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		yearOfArrivingHk: Yup.string().when('placeOfBirth', {
			is: (placeOfBirth: string) => placeOfBirth && !placeOfBirthIsHK(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	useEffect(() => {
		if (details)
			methods.reset({ ...details.data, ...toAgeFormValues(details.data) });
	}, [details]);

	const watchPlaceOfBirth = methods.watch('placeOfBirth');

	const placeOfBirthIsOther = () => {
		const obj = placeBirthDicList?.data.find(
			(item: any) => item.id == watchPlaceOfBirth
		);
		if (obj && obj.labelEn === 'Others') return true;
		return false;
	};
	const placeOfBirthIsHK = () => {
		const obj = placeBirthDicList?.data.find(
			(item: any) => item.id == watchPlaceOfBirth
		);
		if (obj && obj.labelEn === 'Hong Kong') return true;
		return false;
	};

	const onSubmit: SubmitHandler<EditPersonalRequest> = async (data) => {
		const obj: EditPersonalRequest = { ...data, ...toAgePayload(data) };
		const response = await editPersonal(obj);

		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				`/member-management/${
					type == 'member' ? 'member-list' : 'admission-request-list'
				}/detail/${admissionRequestId}`
			);
		}
	};

	const handleClose = () => {
		navigate(
			`/member-management/${
				type == 'member' ? 'member-list' : 'admission-request-list'
			}/detail/${admissionRequestId}`,
			{ replace: true }
		);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h4'>
								{type == 'admissionRequest'
									? t('drawerListItems.editAdmissionRequest')
									: t('drawerListItems.editMember')}
							</Typography>
						</Box>
						<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
							<Box
								display='flex'
								justifyContent='space-between'
								marginBottom={2}
							>
								<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
									{t('member.personalInformation')}
								</Typography>
							</Box>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='nameEn'
										label={t('preAdmission.name_en') + '*'}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='nameZh'
										label={t('preAdmission.name_zh') + '*'}
									/>
								</Grid>
								<AppAgeInput control={methods.control} />
								<Grid size={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('preAdmission.sex') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='sex'
										row
										options={SEX_LIST}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='hkid'
										label={t('preAdmission.HKID')}
									/>
								</Grid>
								<Grid size={6}>
									<AppAutoCompleteMenu
										control={methods.control}
										name='dialect'
										label={t('preAdmission.dialect')}
										options={(dialectDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='maritalStatus'
										label={t('preAdmission.maritalStatus')}
										options={(maritalStatusDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}></Grid>
								<Grid size={6}>
									<AppDropDownMenu
										control={methods.control}
										name='placeOfBirth'
										label={t('preAdmission.placeBirth')}
										options={(placeBirthDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
										handleChange={() => {
											methods.clearErrors('placeOfBirthSpecify');
											methods.clearErrors('yearOfArrivingHk');
											methods.setValue('placeOfBirthSpecify', '');
											methods.setValue('yearOfArrivingHk', '');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										disabled={!placeOfBirthIsOther()}
										control={methods.control}
										name='placeOfBirthSpecify'
										label={`${t('preAdmission.placeBirthSpecify')}${
											!placeOfBirthIsOther() ? '' : '*'
										}`}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										disabled={placeOfBirthIsHK()}
										control={methods.control}
										name='yearOfArrivingHk'
										label={`${t('preAdmission.yearArrivingHK')}${
											placeOfBirthIsHK() ? '' : '*'
										}`}
									/>
								</Grid>
							</Grid>
							<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
								<Button onClick={handleClose}>{t('button.cancel')}</Button>
								<Button
									type='submit'
									variant='contained'
									endIcon={<CheckCircleOutlineIcon />}
								>
									{t('button.complete')}
								</Button>
							</CardActions>
						</Card>
					</form>
				</FormProvider>
			</Box>
		</>
	);
}
